"use client"
import styled from "@emotion/styled"
import { Box } from "@mui/material"
import Button, { ButtonProps } from "@mui/material/Button"
import Link from "next/link"
import { usePathname } from "next/navigation"
import React from "react"
import { iconsMap } from "../../../public/icons/iconsMap"
import { colors, sizes } from "../../theme/theme"

export const colorPalletteIndex = [
  "turquoise",
  "red",
  "orange",
  "blue",
  "darkBlue",
  "darkOrange",
]
export type colorPalletteIndexType =
  | "turquoise"
  | "red"
  | "orange"
  | "blue"
  | "darkBlue"
  | "darkOrange"

export interface ButtonWithArrowProps extends ButtonProps {
  children?: React.ReactNode | string | undefined
  onClick?: (e: any) => void
  variant?: "elevated" | "outlined"
  arrowName?: string
  ArrowWithLine?: boolean
  colorPallette?: colorPalletteIndexType
  href?: string
  target?: string
  setActive?: boolean
}

export const ArrowNoLineIndex = {
  default: {
    turquoise: "arrow_lightTurquoise_white",
    red: "arrow_lightPink_white",
    orange: "arrow_lightOrange_white",
    blue: "arrow_bluishGray_white",
    darkBlue: "arrow_bluishGray_white",
    darkOrange: "arrow_orange_white",
  },
  elevated: {
    turquoise: "arrow_lightTurquoise_black",
    red: "arrow_lightPink_black",
    orange: "arrow_lightOrange_black",
    blue: "arrow_bluishGray_black",
    darkBlue: "arrow_bluishGray_black",
    darkOrange: "arrow_lightPink_black",
  },
  outlined: {
    turquoise: "arrow_lightTurquoise_white",
    red: "arrow_lightPink_white",
    orange: "arrow_lightOrange_white",
    blue: "arrow_bluishGray_white",
    darkBlue: "arrow_bluishGray_white",
    darkOrange: "arrow_orange_black",
  },
}
const ArrowIndex = {
  default: {
    turquoise: "arrow_lightTurquoise_white_line",
    red: "arrow_lightPink_white_line",
    orange: "arrow_lightOrange_white_line",
    blue: "arrow_bluishGray_white_line",
    darkBlue: "arrow_bluishGray_white_line",
    darkOrange: "arrow_lightOrange_white_line",
  },
  elevated: {
    turquoise: "arrow_lightTurquoise_black_line",
    red: "arrow_lightPink_black_line",
    orange: "arrow_lightOrange_black_line",
    blue: "arrow_bluishGray_black_line",
    darkBlue: "arrow_bluishGray_black_line",
    darkOrange: "arrow_lightOrange_black_line",
  },
  outlined: {
    turquoise: "arrow_lightTurquoise_white_line",
    red: "arrow_lightPink_white_line",
    orange: "arrow_lightOrange_white_line",
    blue: "arrow_bluishGray_white_line",
    darkBlue: "arrow_bluishGray_white_line",
    darkOrange: "arrow_lightOrange_white_line",
  },
}

const StyledButtonWithArrow = styled(Button)`
  svg {
    flex: 0 0 auto;
    width: 31px;
    height: auto;
  }
`

const ButtonWithArrow: React.FC<ButtonWithArrowProps> = ({
  children,
  onClick,
  variant,
  colorPallette,
  ArrowWithLine = true,
  arrowName,
  className,
  setActive,
  href,
  ...props
}) => {
  const pathname = usePathname()
  // Fix: Only consider it active if href is a non-empty string and matches the current path
  const isActive =
    setActive ||
    (href &&
      href !== "" &&
      pathname === href.replace("/pages/", "/") &&
      pathname.includes("actions"))
  const colorPalletteIcon = colorPallette
    ? ArrowWithLine
      ? ArrowIndex[variant || "default"]?.[colorPallette]
      : ArrowNoLineIndex[variant || "default"]?.[colorPallette]
    : "arrow_orange_white_line" // default icon
  const iconName = arrowName || colorPalletteIcon
  const Arrow = iconsMap[iconName] || iconsMap.arrow_orange_white_line // default icon without type restriction
  const combinedClassName = `${className || ""} ${colorPallette || ""} ${isActive ? "active" : ""}`

  return (
    <StyledButtonWithArrow
      variant={variant}
      onClick={onClick}
      className={combinedClassName.trim()}
      LinkComponent={Link}
      {...(href ? { href } : {})}
      {...props}
    >
      {children}
      <Arrow className="arrow-icon" />
    </StyledButtonWithArrow>
  )
}

export default ButtonWithArrow

const StyledTallButtonWithArrow = styled(ButtonWithArrow)`
  font-size: 16px;
  border-radius: ${sizes.borderRadius};
  padding: 16px;
  .content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    width: 80%;
  }
  .title {
    color: ${colors.black};
  }

  &.active {
    opacity: 0.8;
    pointer-events: none;
    position: relative;

    &:after {
      content: "";
      position: absolute;
      bottom: -4px;
      left: 0;
      width: 100%;
      height: 2px;
      background-color: currentColor;
    }
  }
`
export const ButtonWithArrowTall: React.FC<ButtonWithArrowProps> = ({
  children,
  onClick,
  variant,
  arrowName,
  colorPallette,
  ...props
}) => {
  return (
    <StyledTallButtonWithArrow
      variant={variant}
      onClick={onClick}
      LinkComponent={Link}
      arrowName={arrowName}
      target={props.target ? props.target : undefined}
      className={`${props.className || ""} ${colorPallette ? colorPallette : ""}`}
      colorPallette={colorPallette}
      {...props}
    >
      <Box className="content">{children}</Box>
    </StyledTallButtonWithArrow>
  )
}
